import { useState } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';

interface CategorySidebarProps {
  tiles: TileConfig[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
}

const CATEGORIES = ['All', 'Apps', 'Tools', 'Verticals', 'Admin'];

export default function CategorySidebar({ tiles, activeCategory, onCategoryChange }: CategorySidebarProps) {
  const [search, setSearch] = useState('');

  const countFor = (cat: string) =>
    cat === 'All'
      ? tiles.length
      : tiles.filter((tile) => tile.tags.includes(cat)).length;

  const filtered = CATEGORIES.filter(
    (c) => !search || c.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <aside
      style={{
        width: 'var(--shell-sidebar-w)',
        background: 'var(--bg-recessed)',
        borderRightWidth: '1px',
        borderRightStyle: 'solid',
        borderRightColor: 'var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-5)',
        alignSelf: 'flex-start',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <input
        type="search"
        placeholder={t('sidebar.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          background: 'var(--bg-surface)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--border-default)',
          borderRadius: 'var(--radius-md)',
          padding: '6px var(--sp-3)',
          fontFamily: 'var(--font-sans)',
          fontSize: '14px',
          color: 'var(--fg-1)',
          width: '100%',
        }}
      />

      <p className="dp-eyebrow" style={{ padding: '0 var(--sp-2)' }}>
        {t('sidebar.eyebrow')}
      </p>

      <ul role="list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
        {filtered.map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <li key={cat}>
              <button
                onClick={() => onCategoryChange(cat)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  background: isActive ? 'var(--bg-surface)' : 'transparent',
                  boxShadow: isActive ? 'var(--shadow-1)' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-sm)',
                  fontWeight: isActive ? 500 : 400,
                  color: isActive ? 'var(--fg-1)' : 'var(--fg-2)',
                  textAlign: 'left',
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
              >
                <span>{cat}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-4)' }}>
                  {countFor(cat)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
